/**
 * WebAuthn PRF re-unlock helper (Task #101).
 *
 * Wraps the @simplewebauthn/browser ceremonies and the PRF extension into
 * three high-level operations:
 *
 *   - `isWebauthnPrfSupported()`          — feature detection
 *   - `enrollWebauthnCredential(name)`    — register this device + wrap MEK
 *   - `unlockWithWebauthn()`              — biometric tap → unwrap MEK → unlock
 *
 * Threat model: the PRF output exists only transiently in JS memory inside
 * this module to derive the AES-256 KEK and unwrap the MEK. It is never
 * written to localStorage / sessionStorage / IndexedDB / cookies / a Service
 * Worker, never logged, and never sent to the server. The only things stored
 * server-side are the credential ID, COSE public key, non-secret PRF salt,
 * and the AES-GCM wrappedMek ciphertext — none of which decrypt without the
 * physical authenticator.
 */

import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";
import { apiRequest } from "@/lib/queryClient";
import {
  aesGcmDecrypt,
  aesGcmEncrypt,
  b64ToBytes,
  bytesToB64,
  type WrappedBlob,
} from "./crypto";
import { getMek, unlockVault } from "./vaultState";
import { fetchVault } from "./vaultClient";

export interface WebauthnCredentialView {
  id: string;
  nickname: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export class WebauthnUnlockError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "WebauthnUnlockError";
  }
}

/**
 * Strip the PRF output from the WebAuthn response BEFORE we hand the
 * response to the server. The server only needs the signature + attestation
 * bits to verify the ceremony; `clientExtensionResults.prf.results.first`
 * is the actual KEK material and must never traverse the wire (threat
 * model). We keep `prf.enabled` / `prf.results.second` shape but drop the
 * raw bytes.
 */
function stripPrfResults<T extends { clientExtensionResults?: any }>(resp: T): T {
  const ext = resp.clientExtensionResults;
  if (!ext || !ext.prf) return resp;
  const sanitizedPrf: any = {};
  if (typeof ext.prf.enabled === "boolean") sanitizedPrf.enabled = ext.prf.enabled;
  return {
    ...resp,
    clientExtensionResults: {
      ...ext,
      prf: sanitizedPrf,
    },
  };
}

// base64url -> Uint8Array (the WebAuthn JSON variant of base64).
function b64urlToBytes(s: string): Uint8Array {
  const pad = s.length % 4 === 2 ? "==" : s.length % 4 === 3 ? "=" : "";
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + pad;
  return b64ToBytes(b64);
}

/**
 * Coerce a PRF salt/result value into a Uint8Array, regardless of whether
 * it arrived as a base64url string (over the JSON wire from our server),
 * an ArrayBuffer (from `navigator.credentials.*` extension results), or
 * an already-typed view. This is the single chokepoint that reconciles
 * "wire format = base64url string" with "native WebAuthn API format =
 * BufferSource" — the source of Task #110's TypeError was the absence of
 * exactly this coercion.
 */
function toBytes(value: unknown): Uint8Array {
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) {
    const v = value as ArrayBufferView;
    return new Uint8Array(v.buffer, v.byteOffset, v.byteLength);
  }
  if (typeof value === "string") return b64urlToBytes(value);
  throw new WebauthnUnlockError("Unexpected PRF value shape from authenticator.");
}

/**
 * Walk a registration-options JSON envelope and replace any `prf.eval.first`
 * / `prf.eval.second` values that came across as base64url strings with the
 * `Uint8Array` form the native WebAuthn API requires. `@simplewebauthn/browser`
 * v13's `startRegistration` does NOT touch `extensions` — it spreads the JSON
 * straight into `navigator.credentials.create()`, which expects BufferSources
 * here. Leaving the salt as a string trips a TypeError inside the browser
 * implementation (`.replace` on non-string) on every platform.
 */
function normalizePrfExtensionsInOptions(options: any): void {
  const prf = options?.extensions?.prf;
  if (!prf) return;
  if (prf.eval) {
    if ("first" in prf.eval && prf.eval.first != null) {
      prf.eval.first = toBytes(prf.eval.first);
    }
    if ("second" in prf.eval && prf.eval.second != null) {
      prf.eval.second = toBytes(prf.eval.second);
    }
  }
  if (prf.evalByCredential && typeof prf.evalByCredential === "object") {
    for (const credId of Object.keys(prf.evalByCredential)) {
      const e = prf.evalByCredential[credId];
      if (!e) continue;
      if ("first" in e && e.first != null) e.first = toBytes(e.first);
      if ("second" in e && e.second != null) e.second = toBytes(e.second);
    }
  }
}

/**
 * Quick feature-detection. Heuristic: the browser must support WebAuthn
 * and conditional UI must be possible OR a platform authenticator must
 * exist. We can only *definitely* know PRF works after a real ceremony, so
 * enrollment also verifies prf.results.first came back populated.
 */
export async function isWebauthnPrfSupported(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (!browserSupportsWebAuthn()) return false;
  // Heuristic gate — Safari/Chrome/Edge on iOS 17+, macOS 14+, Android 14+,
  // Windows 11 22H2+, and recent Linux + Yubikey support PRF. We let the
  // ceremony itself be the source of truth; this just hides the UI on
  // browsers that obviously can't do WebAuthn at all.
  try {
    const fn = (PublicKeyCredential as any).isUserVerifyingPlatformAuthenticatorAvailable;
    if (typeof fn === "function") {
      const platformOK = await (PublicKeyCredential as any).isUserVerifyingPlatformAuthenticatorAvailable();
      if (platformOK) return true;
    }
  } catch {
    // ignore — we'll still return true to give external authenticators a chance
  }
  return true;
}

export async function listWebauthnCredentials(): Promise<WebauthnCredentialView[]> {
  const res = await apiRequest("GET", "/api/vault/webauthn/credentials");
  return (await res.json()) as WebauthnCredentialView[];
}

export async function removeWebauthnCredential(id: string): Promise<void> {
  await apiRequest("DELETE", `/api/vault/webauthn/credentials/${encodeURIComponent(id)}`);
}

/**
 * Derive a 32-byte AES key from the raw PRF output. We hash through
 * SHA-256 so that even if a future authenticator returned a non-uniform
 * 32-byte buffer the resulting KEK is still uniformly distributed.
 */
async function derivePrfKek(prfBytes: Uint8Array): Promise<Uint8Array> {
  const digest = await crypto.subtle.digest("SHA-256", prfBytes as BufferSource);
  return new Uint8Array(digest);
}

/**
 * SHA-256 of the PRF output, sent to the server as a one-way proof that the
 * authenticator actually returned a PRF result. The hash is computed over a
 * domain-separation prefix so it can't be confused with any other use of
 * SHA-256 over the same bytes elsewhere in the codebase.
 */
async function derivePrfWitnessB64(prfBytes: Uint8Array): Promise<string> {
  const prefix = new TextEncoder().encode("recoverme-vault-prf-witness-v1|");
  const combined = new Uint8Array(prefix.length + prfBytes.length);
  combined.set(prefix, 0);
  combined.set(prfBytes, prefix.length);
  const digest = await crypto.subtle.digest("SHA-256", combined as BufferSource);
  combined.fill(0);
  return bytesToB64(new Uint8Array(digest));
}

/**
 * Enroll the current device as a quick-unlock authenticator. The vault MUST
 * already be unlocked in this tab — we wrap a copy of the in-memory MEK with
 * a KEK derived from the authenticator's PRF output.
 */
export async function enrollWebauthnCredential(nickname: string): Promise<WebauthnCredentialView> {
  const mek = getMek();
  if (!mek) {
    throw new WebauthnUnlockError("Unlock your vault with your password first, then try again.");
  }

  const beginRes = await apiRequest("POST", "/api/vault/webauthn/enroll/begin", { nickname });
  const beginJson = await beginRes.json();
  const options = beginJson.options as Parameters<typeof startRegistration>[0]["optionsJSON"];
  // The server ships PRF salts as base64url strings (correct for the JSON
  // wire). The native WebAuthn API on the other side of `startRegistration`
  // requires BufferSources — convert in place before the call.
  normalizePrfExtensionsInOptions(options);

  let registration: Awaited<ReturnType<typeof startRegistration>>;
  try {
    registration = await startRegistration({ optionsJSON: options });
  } catch (err: any) {
    if (err?.name === "NotAllowedError" || err?.name === "AbortError") {
      throw new WebauthnUnlockError("Enrollment cancelled.");
    }
    throw new WebauthnUnlockError(err?.message || "Could not enroll this device.");
  }

  // Confirm the authenticator actually evaluated the PRF. If not we fail
  // closed — the server-side /finish endpoint also enforces this, but
  // checking here lets us derive the KEK before posting.
  //
  // `prf.results.first` comes back from the browser as an ArrayBuffer (per
  // the WebAuthn L3 spec); `toBytes()` accepts ArrayBuffer | Uint8Array |
  // base64url string so we're tolerant of either shape if a future browser
  // ever normalizes to a string.
  const prfFirstRaw = (registration.clientExtensionResults as any)?.prf?.results?.first;
  if (prfFirstRaw == null) {
    throw new WebauthnUnlockError("This device's biometric doesn't support quick-unlock (no PRF). Use password unlock.");
  }
  const prfBytes = toBytes(prfFirstRaw);
  const prfWitnessB64 = await derivePrfWitnessB64(prfBytes);
  const kek = await derivePrfKek(prfBytes);
  // Wipe the raw PRF bytes as soon as we've derived the KEK + witness.
  prfBytes.fill(0);

  let wrappedMek: WrappedBlob;
  try {
    wrappedMek = await aesGcmEncrypt(kek, mek);
  } finally {
    kek.fill(0);
  }

  // PRF output is stripped before the response is sent — server must only
  // ever see the signature/attestation + one-way prfWitness, never the KEK.
  const finishRes = await apiRequest("POST", "/api/vault/webauthn/enroll/finish", {
    response: stripPrfResults(registration),
    nickname,
    wrappedMek,
    prfWitnessB64,
  });
  return (await finishRes.json()) as WebauthnCredentialView;
}

/**
 * Re-unlock the vault using the current device's enrolled authenticator.
 * Single biometric tap. Falls back to throwing — callers should catch and
 * surface a "Use password instead" option.
 */
export async function unlockWithWebauthn(): Promise<void> {
  const beginRes = await apiRequest("POST", "/api/vault/webauthn/assert/begin", {});
  const beginJson = await beginRes.json();
  const options = beginJson.options as Parameters<typeof startAuthentication>[0]["optionsJSON"] & Record<string, any>;
  const prfSaltsByCredId: Record<string, string> = beginJson.prfSaltsByCredentialIdB64url ?? {};

  // Build per-credential PRF inputs. The WebAuthn spec lets us pass an
  // `evalByCredential` map so each enrolled credential evaluates against
  // its own salt — important because we want each device's wrapped MEK to
  // be derivable only by that device. The native API requires BufferSources
  // here; `normalizePrfExtensionsInOptions` then no-ops because the values
  // are already Uint8Arrays.
  const evalByCredential: Record<string, { first: Uint8Array }> = {};
  for (const [credId, saltB64url] of Object.entries(prfSaltsByCredId)) {
    evalByCredential[credId] = { first: b64urlToBytes(saltB64url) };
  }
  (options as any).extensions = {
    ...(options.extensions ?? {}),
    prf: {
      evalByCredential,
    },
  };
  normalizePrfExtensionsInOptions(options);

  let assertion: Awaited<ReturnType<typeof startAuthentication>>;
  try {
    assertion = await startAuthentication({ optionsJSON: options });
  } catch (err: any) {
    if (err?.name === "NotAllowedError" || err?.name === "AbortError") {
      throw new WebauthnUnlockError("Biometric cancelled.");
    }
    throw new WebauthnUnlockError(err?.message || "Quick unlock failed.");
  }

  // PRF result is an ArrayBuffer per the WebAuthn L3 spec (NOT a base64url
  // string). Reading it as a string here was the second half of the
  // Task #110 regression — the chained `.replace()` inside the previous
  // decoder tripped a TypeError on every unlock attempt.
  const prfFirstRaw = (assertion.clientExtensionResults as any)?.prf?.results?.first;
  if (prfFirstRaw == null) {
    // No silent fallback — fail closed and let the caller offer password.
    throw new WebauthnUnlockError("Your device didn't return a quick-unlock secret. Use password instead.");
  }
  const prfBytes = toBytes(prfFirstRaw);
  const prfWitnessB64 = await derivePrfWitnessB64(prfBytes);
  const kek = await derivePrfKek(prfBytes);
  prfBytes.fill(0);

  let wrappedMek: WrappedBlob;
  try {
    const finishRes = await apiRequest("POST", "/api/vault/webauthn/assert/finish", {
      response: stripPrfResults(assertion),
      prfWitnessB64,
    });
    const finishJson = await finishRes.json();
    wrappedMek = finishJson.wrappedMek as WrappedBlob;
    if (!wrappedMek || typeof wrappedMek.ciphertextB64 !== "string") {
      throw new WebauthnUnlockError("Server didn't return wrapped key material.");
    }
  } catch (err) {
    kek.fill(0);
    throw err;
  }

  let mek: Uint8Array;
  try {
    mek = await aesGcmDecrypt(kek, wrappedMek);
  } catch {
    kek.fill(0);
    throw new WebauthnUnlockError("Could not unwrap your vault key. The device may have been re-enrolled — use password.");
  }
  kek.fill(0);

  // We still need the X25519 private key — fetch the vault row and unwrap
  // it with the freshly-recovered MEK exactly like the password path does.
  const vault = await fetchVault();
  if (!vault) {
    mek.fill(0);
    throw new WebauthnUnlockError("Vault not found on the server.");
  }
  const priv = await aesGcmDecrypt(mek, vault.wrappedX25519PrivateKey);
  const pub = b64ToBytes(vault.publicX25519Key);
  unlockVault(mek, { privateKey: priv, publicKey: pub });
  try {
    await apiRequest("POST", "/api/vault/unlocked", {});
  } catch {
    // non-fatal
  }
}

// Re-export for callers that want the typed result of bytesToB64 elsewhere.
export { bytesToB64 };
